import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import * as Yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { formatISO, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
 Heading,
 Button,
 Flex,
 Modal,
 ModalContent,
 ModalOverlay,
 useToast,
 Grid,
 Box,
 FormLabel,
 InputGroup,
 Input,
 InputRightElement,
 Image,
} from '@chakra-ui/react';
import baseService from '../../services/base';
import { SuccessModal } from '../Modals';
import { fetchData } from '../../services/fetch';
import { Translator } from '../Language';
import { Select } from '../Input';
import { calendarInput } from '../../assets/new-images/icons';

registerLocale('pt-BR', ptBR);

const schema = Yup.object().shape({
 startDate: Yup.date().required(<Translator path={'global.error.required'} />),
 deadline: Yup.date()
  .required(<Translator path={'global.error.required'} />)
  .min(Yup.ref('startDate'), 'A data final deve ser posterior à data inicial'),
});

export default function CertificationSelectModal({
 onClose,
 isOpen,
 isEditing,
 companieCertifications,
 idCompanyGroup,
 editCertification,
 mutate,
 userId,
 companyId,
 roleId,
}) {
 const toast = useToast();
 const [selectedCertification, setSelectedCertification] = useState(null);
 const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);

 const defaultValues = {
  startDate: editCertification?.period
   ? parse(editCertification.period, "yyyy-MM-dd'T'HH:mm:ss.SSSX", new Date())
   : undefined,
  deadline: editCertification?.end_application_date
   ? parse(
      editCertification.end_application_date,
      "yyyy-MM-dd'T'HH:mm:ss.SSSX",
      new Date()
     )
   : undefined,
 };

 const {
  control,
  reset,
  handleSubmit,
  watch,
  formState: { isValid, isDirty, isSubmitting },
 } = useForm({
  resolver: yupResolver(schema),
  defaultValues,
 });

 const startDate = watch('startDate');

 const { data: certificationsList, isLoading } = useSWR(`/certificate`, fetchData);

 const certificationOptions = useMemo(() => {
  const existingCertificationIds =
   companieCertifications?.map((cert) => cert.id_certificate) || [];
  const filtered = certificationsList?.filter((cert) => !existingCertificationIds.includes(cert?.id)) || [];

  const grouped = filtered.reduce((acc, certification) => {
   const key = `${certification.name}__${certification.exercise ?? ''}`;
   if (!acc[key] || certification.id > acc[key].id) {
    acc[key] = certification;
   }
   return acc;
  }, {});

  return Object.values(grouped).map((certification) => ({
   label: `${certification.name}${certification.exercise ? ` (${certification.exercise})` : ''}${certification.project_name ? ` - ${certification.project_name}` : ''}`,
   value: certification.id,
  }));
 }, [certificationsList, companieCertifications]);

 const handleQuestionnaireSelect = (value) => {
  setSelectedCertification(value);
 };

 const showErrorToast = (message) => {
  toast({
   title: 'Erro',
   description: message,
   status: 'error',
  });
 };

 async function onSubmit(data) {
  try {
   const { startDate, deadline } = data;
   const reference_month_year = formatISO(startDate);
   const end_application_date = formatISO(deadline);

   // First API call
   if (isEditing) {
    await baseService.patch(
     `/certificate/linkCertificateCompany/${editCertification?.id_link}`,
     {
      end_application_date,
      reference_month_year,
     }
    );
   } else {
    await baseService.post(`certificate/link-group`, {
     id_certificate: +selectedCertification,
     id_company_group: idCompanyGroup,
     end_application_date,
     reference_month_year,
    });
   }

   try {
    await baseService.post(
     `certificate-status-control?certificateId=${selectedCertification}&userId=${userId}&roleId=${1}&companyId=${companyId}&companyGroupId=${idCompanyGroup}`
    );
   } catch (err) {
    console.error('Error in status control creation:', err);
   }

   mutate();
   setIsSubmitSuccessful(true);
  } catch (err) {
   showErrorToast(
    'Não foi possível adicionar a certificação. Tente novamente mais tarde.'
   );
  }
 }

 const handleClose = () => {
  setSelectedCertification(null);
  onClose();
  reset();
  setIsSubmitSuccessful(false);
 };

 const isButtonDisabled = isEditing
  ? !isValid || !isDirty
  : !isValid || !selectedCertification;

 if (isLoading) {
  return null;
 }

 if (isLoading) {
  return null;
 }

 return isSubmitSuccessful ? (
  <SuccessModal
   isOpen={isOpen}
   onClose={handleClose}
   title={`Certificação ${isEditing ? 'editada' : 'adicionada'} com sucesso`}
  />
 ) : (
  <Modal isOpen={isOpen} onClose={handleClose} size='3xl' autoFocus={false}>
   <ModalOverlay />
   <ModalContent
    as='form'
    onSubmit={handleSubmit(onSubmit)}
    width='640px'
    borderRadius='12px'
    p='32px'
   >
    <Heading as='h3' fontSize='24px' lineHeight='28.18px' mb='32px'>
     {isEditing ? 'Editar certificação' : 'Adicionar certificação'}
    </Heading>

    <Select
     label='Certificação'
     placeholder={
      editCertification
       ? editCertification?.name
       : certificationOptions?.length === 0
       ? 'Nenhuma certificação disponível'
       : 'Selecione uma certificação'
     }
     options={certificationOptions}
     onChange={(e) => handleQuestionnaireSelect(e.target.value)}
     isDisabled={isEditing}
     h='50px'
     labelFontSize='12px'
    />

    <Grid templateColumns='repeat(2, 1fr)' gap={4} mt='6' mb='32px'>
     <Box>
      <FormLabel htmlFor='startDate' fontSize='12px'>
       Prazo inicial
      </FormLabel>
      <Controller
       name='startDate'
       control={control}
       render={({ field }) => (
        <InputGroup display='grid' isolation='unset'>
         <DatePicker
          {...field}
          selected={field.value}
          onChange={(date) => field.onChange(date)}
          minDate={new Date()}
          dateFormat='dd/MM/yyyy'
          locale='pt-BR'
          placeholderText='Selecione a data inicial'
          autoComplete='off'
          customInput={
           <Input
            value={field?.value}
            aria-label='Selecione'
            autoComplete='off'
            _autofill={{
             bg: '#F5F5FB !important',
             WebkitBoxShadow: '0 0 0px 1000px #F5F5FB inset',
             WebkitTextFillColor: '#000000',
             transition: 'background-color 5000s ease-in-out 0s',
            }}
            type='text'
            _focusVisible={{
             borderColor: 'inherit',
            }}
            _disabled={{
             bg: '#E5E5E5',
             borderColor: '#DCCFEC',
             cursor: 'not-allowed',
            }}
            readOnly
           />
          }
         />
         <InputRightElement
          pointerEvents='none'
          display={'flex'}
          alignItems='center'
          h='100%'
          cursor='pointer'
         >
          <Image src={calendarInput} alt='calendar' mr='10px' height='23px' />
         </InputRightElement>
        </InputGroup>
       )}
      />
     </Box>

     <Box>
      <FormLabel htmlFor='deadline' fontSize='12px'>
       Prazo final
      </FormLabel>
      <Controller
       name='deadline'
       control={control}
       render={({ field }) => (
        <InputGroup display='grid' isolation='unset'>
         <DatePicker
          {...field}
          selected={field.value}
          onChange={(date) => field.onChange(date)}
          minDate={startDate || new Date()}
          dateFormat='dd/MM/yyyy'
          locale='pt-BR'
          placeholderText='Selecione a data final'
          autoComplete='off'
          disabled={!startDate}
          customInput={
           <Input
            value={field?.value}
            aria-label='Selecione'
            autoComplete='off'
            _autofill={{
             bg: '#F5F5FB !important',
             WebkitBoxShadow: '0 0 0px 1000px #F5F5FB inset',
             WebkitTextFillColor: '#000000',
             transition: 'background-color 5000s ease-in-out 0s',
            }}
            type='text'
            _focusVisible={{
             borderColor: 'inherit',
            }}
            _disabled={{
             bg: '#E5E5E5',
             borderColor: '#DCCFEC',
             cursor: 'not-allowed',
            }}
            readOnly
           />
          }
         />
         <InputRightElement
          pointerEvents='none'
          display={'flex'}
          alignItems='center'
          h='100%'
          cursor='pointer'
         >
          <Image src={calendarInput} alt='calendar' mr='10px' height='23px' />
         </InputRightElement>
        </InputGroup>
       )}
      />
     </Box>
    </Grid>

    <Flex gap='24px' justify={'space-between'} w='100%'>
     <Button variant='cancel' flex='1' height='55px' onClick={handleClose}>
      Cancelar
     </Button>
     <Button
      isLoading={isSubmitting}
      isDisabled={isButtonDisabled}
      type='submit'
      flex='1'
      height='55px'
      variant='primary'
     >
      {isEditing ? 'Salvar' : 'Adicionar'}
     </Button>
    </Flex>
   </ModalContent>
  </Modal>
 );
}
